import React, { useEffect, useState } from 'react';
import SEO from './SEO';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getTrackingRequests } from '../services/firebase';
import { getWallet } from '../services/api';
import ApiAccessSection from './ApiAccessSection';
import Header from './Header';
import Footer from './Footer';
import './ProfilePage.css';

function formatDate(ts) {
  if (!ts) return '—';
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  return date.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function ProfilePage() {
  const { user, creditBalance, openPaymentModal, logout } = useAuth();
  const navigate = useNavigate();

  const [trackingHistory, setTrackingHistory] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!user) { navigate('/'); return; }
    (async () => {
      const [walletRes, requests] = await Promise.allSettled([
        getWallet(user.uid),
        getTrackingRequests(user.uid),
      ]);
      if (walletRes.status === 'fulfilled') setWallet(walletRes.value.wallet ?? null);
      else console.error('Wallet fetch failed:', walletRes.reason);
      if (requests.status === 'fulfilled') setTrackingHistory(requests.value);
      else console.error('Tracking history failed:', requests.reason);
      setIsLoading(false);
    })();
  }, [user, navigate]);

  const handleTrackAgain = (trackingNumber) => {
    navigate(`/results?tracking=${encodeURIComponent(trackingNumber)}`);
  };

  if (!user) return null;
  return (
    <div className="profile-page">
      <SEO
        title="My Account"
        description="Manage your RideLink Tracking account, view your credit balance, and review your shipment tracking history."
        canonicalPath="/profile"
      />
      <Header />

      <main className="profile-main">
        <div className="profile-container">

          {/* ── Account card ───────────────────────────────────── */}
          <div className="profile-card profile-account">
            <div className="profile-avatar-wrap">
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="profile-avatar" referrerPolicy="no-referrer" />
              ) : (
                <div className="profile-avatar-initials">
                  {user.name?.charAt(0).toUpperCase() || 'U'}
                </div>
              )}
            </div>
            <div className="profile-account-info">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                <div>
                  <h1 className="profile-name">{user.name}</h1>
                  <p className="profile-email">{user.email}</p>
                </div>
                <button 
                  onClick={() => {
                    logout();
                    navigate('/');
                  }} 
                  style={{
                    background: 'transparent',
                    border: '1px solid #E2E8F0',
                    padding: '6px 14px',
                    borderRadius: '8px',
                    color: '#64748B',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontSize: '13px',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => { e.target.style.background = '#F1F5F9'; e.target.style.color = '#334155'; }}
                  onMouseOut={(e) => { e.target.style.background = 'transparent'; e.target.style.color = '#64748B'; }}
                >
                  Sign Out
                </button>
              </div>
            </div>
          </div>

          {/* ── Wallet + stats row ─────────────────────────────── */}
          <div className="profile-stats-row">
            <div className="profile-card profile-wallet">
              <p className="profile-stat-label">Sea credits</p>
              <p className="profile-stat-value profile-balance">{isLoading ? '—' : (wallet?.sea ?? 0)}</p>
              <p className="profile-stat-sub">balance · 1 tracking = 1 credit</p>
              <button className="profile-buy-btn" onClick={openPaymentModal}>
                Buy Credits
              </button>
            </div>

            <div className="profile-card profile-stat-card">
              <p className="profile-stat-label">Air credits</p>
              <p className="profile-stat-value">{isLoading ? '—' : (wallet?.air ?? 0)}</p>
              <p className="profile-stat-sub">balance</p>
            </div>

            <div className="profile-card profile-stat-card">
              <p className="profile-stat-label">Sea purchased</p>
              <p className="profile-stat-value">{isLoading ? '—' : (wallet?.totalSeaPurchased ?? 0)}</p>
              <p className="profile-stat-sub">credits bought</p>
            </div>

            <div className="profile-card profile-stat-card">
              <p className="profile-stat-label">Sea used</p>
              <p className="profile-stat-value">{isLoading ? '—' : (wallet?.totalSeaUsed ?? 0)}</p>
              <p className="profile-stat-sub">credits consumed</p>
            </div>
          </div>

          {/* ── Developer API access ───────────────────────────── */}
          <ApiAccessSection />

          {/* ── Tracking history ───────────────────────────────── */}
          <div className="profile-card profile-history">
            <h2 className="profile-section-title">Tracking History</h2>

            {isLoading ? (
              <div className="profile-loading">
                <div className="profile-spinner" />
                <p>Loading history…</p>
              </div>
            ) : trackingHistory.length === 0 ? (
              <div className="profile-empty">
                <p>No tracking requests yet.</p>
                <button className="profile-buy-btn" onClick={() => navigate('/results')}>
                  Track a shipment
                </button>
              </div>
            ) : (
              <div className="profile-table-wrap">
                <table className="profile-table">
                  <thead>
                    <tr>
                      <th>Tracking #</th>
                      <th>Date</th>
                      <th>Credits</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {trackingHistory.map((req) => (
                      <tr key={req.id}>
                        <td className="profile-tracking-num">{req.trackingNumber || '—'}</td>
                        <td className="profile-date">{formatDate(req.createdAt)}</td>
                        <td>
                          <span className="profile-credit-badge">−1</span>
                        </td>
                        <td>
                          <button
                            className="profile-track-again-btn"
                            onClick={() => handleTrackAgain(req.trackingNumber)}
                          >
                            Track again
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
}
